import { ExternalTransferTypes } from '@generated/graphql';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import type { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import {
  type ActiveOrganizationProps,
  UserContext,
} from '@providers/userContextProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../../hooks/useLocalization';
import { showsRequiredFieldError } from '../depositFieldErrors';

type TransferTypeOption = {
  required?: FieldRequiredType;
  label?: unknown;
};

export type DepositExternalTransferTypeSelectProp = {
  transferType?: string;
  option?: TransferTypeOption;
  focused: string[];
  onFocus: (field: string) => void;
  onChange: (value: Partial<TransferContextData>) => void;
  usTransferFlowEnabled: boolean;
  enableInKind: boolean;
};

const TRANSFER_TYPES = [
  ExternalTransferTypes.AllInCash,
  ExternalTransferTypes.PartialCash,
  ExternalTransferTypes.AllInKind,
];

const TRANSFER_TYPE_MENU_ITEMS = {
  [ExternalTransferTypes.AllInCash]: {
    testId: 'transfer-type-select-all-cash',
    labelKey: 'depositReview.ALL_IN_CASH',
  },
  [ExternalTransferTypes.PartialCash]: {
    testId: 'transfer-type-select-partial-cash',
    labelKey: 'depositReview.PARTIAL_CASH',
  },
  [ExternalTransferTypes.AllInKind]: {
    testId: 'transfer-type-select-all-in-kind',
    labelKey: 'depositReview.ALL_IN_KIND',
  },
} as const;

const allowedTransferTypes = ({
  usTransferFlowEnabled,
  enableInKind,
  organization,
}: {
  usTransferFlowEnabled: boolean;
  enableInKind: boolean;
  organization?: Pick<
    ActiveOrganizationProps,
    | 'enableFullCashTransfers'
    | 'enablePartialCashTransfers'
    | 'enableAllInKindTransfers'
  >;
}) => {
  const allowed: Record<ExternalTransferTypes, boolean | undefined> =
    usTransferFlowEnabled
      ? {
          [ExternalTransferTypes.AllInCash]:
            organization?.enableFullCashTransfers,
          [ExternalTransferTypes.PartialCash]:
            organization?.enablePartialCashTransfers,
          [ExternalTransferTypes.AllInKind]:
            organization?.enableAllInKindTransfers && enableInKind,
        }
      : {
          [ExternalTransferTypes.AllInCash]: true,
          [ExternalTransferTypes.PartialCash]: true,
          [ExternalTransferTypes.AllInKind]: enableInKind,
        };

  return TRANSFER_TYPES.filter((type) => allowed[type]);
};

export const DepositExternalTransferTypeSelect = ({
  transferType,
  option,
  focused,
  onFocus,
  onChange,
  usTransferFlowEnabled,
  enableInKind,
}: DepositExternalTransferTypeSelectProp) => {
  const { t } = useTranslation('workflowCompletions');
  const { translateBackendString } = useLocalization();
  const { activeOrganization } = useContext(UserContext);

  return (
    <SelectField
      fullWidth
      sx={{ mb: 2, mt: 2 }}
      testId='transfer-type-select'
      label={translateBackendString(option?.label)}
      onChange={(e: any) => onChange({ transferType: e.target.value })}
      value={transferType}
      onBlur={() => onFocus('transferType')}
      error={showsRequiredFieldError({
        missing: !transferType,
        field: 'transferType',
        focused,
        requirement: option?.required,
      })}
    >
      {allowedTransferTypes({
        usTransferFlowEnabled,
        enableInKind,
        organization: activeOrganization,
      }).map((type) => (
        <MenuItem
          key={type}
          data-testid={TRANSFER_TYPE_MENU_ITEMS[type].testId}
          value={type}
        >
          {t(TRANSFER_TYPE_MENU_ITEMS[type].labelKey)}
        </MenuItem>
      ))}
    </SelectField>
  );
};
